import { AnalyticsFlowEvent } from "@/analytics/types";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { useFlow } from "@/contexts/FlowContext";
import { useMedia } from "@/contexts/MediaContext";
import { useGetPredictions } from "@/hooks/useGetPredictions";
import Button from "@components/Button";
import { useCallback, useMemo } from "react";
import { useModel } from "@/contexts/ModelContext";
import { BuildSteps, Flows } from "@/types/flows";
import { useAsyncBoundOperation } from "@/hooks/useApi";
import { ErrorInfo } from "@/components/ErrorInfo";
import { useFlowState } from "@/hooks/useFlowState";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { usePageNavigationShortcut } from "@/hooks/usePageNavigationShortcut";

export const BuildModelBtn = ({
    disabled = false,
    loading = false,
    loadingText = "Processing Files..."
}: {
    disabled: boolean;
    loading: boolean;
    loadingText?: string;
}) => {
    const { track } = useAnalytics();
    const { task } = useFlow();
    const { setStep } = useFlowState();
    const { completedMediaItems } = useMedia();
    const { generateModel, isLoadingInferenceQueue, errorInferenceQueue } = useGetPredictions();
    const { annotationsForFileAndFrameId } = useModel();

    const allFilesPromptsCount = useMemo(
        () =>
            Object.values(annotationsForFileAndFrameId).reduce(
                (acc, annotationsForFile) => acc + Object.values(annotationsForFile).length,
                0
            ),
        [annotationsForFileAndFrameId]
    );

    const promptedFilesCount = useMemo(
        () =>
            Object.keys(annotationsForFileAndFrameId).filter(
                (fileId) => Object.values(annotationsForFileAndFrameId[fileId]).length > 0
            ).length,
        [annotationsForFileAndFrameId]
    );

    const generateModelAndAdvance = useCallback(async () => {
        track({
            event: AnalyticsFlowEvent.BUILD_MODEL_CLICKED,
            data: {
                task,
                uploaded_files_count: completedMediaItems.length,
                prompted_files_count: promptedFilesCount,
                prompts_count: allFilesPromptsCount
            }
        });

        try {
            await generateModel();
            // Advance directly to review step after predictions complete
            setStep({ flow: Flows.BUILD, step: BuildSteps.REVIEW });
        } catch (error: any) {
            console.error(error);
            throw error;
        }
    }, [
        allFilesPromptsCount,
        completedMediaItems.length,
        generateModel,
        promptedFilesCount,
        task,
        track,
        setStep
    ]);

    const {
        isLoading: isLoadingGenerateModel,
        error: errorGenerateModel,
        reset: resetGenerateModel,
        invoke: invokeGenerateModel
    } = useAsyncBoundOperation(generateModelAndAdvance);

    const onClickBuildModel = useCallback(() => {
        resetGenerateModel();
        invokeGenerateModel();
    }, [invokeGenerateModel, resetGenerateModel]);

    const isLoading = loading || isLoadingGenerateModel || isLoadingInferenceQueue;
    const error = errorGenerateModel || errorInferenceQueue;

    // Keyboard shortcut: Command + Right Arrow
    usePageNavigationShortcut("right", onClickBuildModel, disabled || isLoading);

    return (
        <div className="flex flex-col gap-4">
            {error && <ErrorInfo error={error} onRetryClick={onClickBuildModel} />}
            <Button
                onClick={onClickBuildModel}
                disabled={disabled || isLoading}
                loading={isLoading}
                loadingText={loadingText}
                className="w-full"
            >
                <i className="far fa-check"></i>
                <span>Review Model</span>
                <div
                    className={twClassNames(
                        "hotkeysContainer",
                        (disabled || isLoading) && "opacity-30"
                    )}
                >
                    <span className="hotkey dark-brand">⌘</span>
                    <span className="hotkey dark-brand">→</span>
                </div>
            </Button>
        </div>
    );
};
